// src/lib/posthog.ts
import type { PostHog, PostHogConfig } from 'posthog-js';
import { API_CONFIG, TELEMETRY_DISABLED } from './config';
import { createDeferredSdk } from './deferredSdk';
import logger from './logger';

const POSTHOG_PROJECT_TOKEN = import.meta.env.VITE_POSTHOG_PROJECT_TOKEN || '';
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';
const IS_DEV = import.meta.env.DEV;

function getEnvironment(): string {
  if (IS_DEV) return 'development';
  if (API_CONFIG.baseURL.includes('staging')) return 'staging';
  return 'production';
}

function getPostHogOptions(): Partial<PostHogConfig> {
  const environment = getEnvironment();

  return {
    api_host: POSTHOG_HOST,
    defaults: '2026-01-30',
    debug: IS_DEV,
    capture_pageview: false,
    capture_pageleave: true,
    respect_dnt: true,
    person_profiles: 'identified_only',
    loaded: (client) => {
      client.register({
        app_environment: environment,
        app_name: 'Insider Performance',
      });
    },
  };
}

const sdk = createDeferredSdk<PostHog>('PostHog', async () => {
  const { default: posthog } = await import('posthog-js');

  posthog.init(POSTHOG_PROJECT_TOKEN, getPostHogOptions());
  logger.log(`PostHog initialized for ${getEnvironment()} environment`);
  return posthog;
});

function isPostHogEnabled(): boolean {
  return Boolean(POSTHOG_PROJECT_TOKEN) && !TELEMETRY_DISABLED;
}

/** Load and initialize the PostHog SDK, then replay anything queued by withPostHog. */
export function loadPostHog(): Promise<void> {
  if (!isPostHogEnabled()) {
    logger.warn('PostHog: disabled or no project token configured - analytics disabled');
    return Promise.resolve();
  }

  return sdk.load();
}

/** Call the PostHog SDK, queueing the call until it has loaded. */
export function withPostHog(action: (posthog: PostHog) => void): void {
  if (isPostHogEnabled()) {
    sdk.run(action);
  }
}
